export interface CurrencyContext {
  baseCode: string;
  code: string;
  symbol: string;
  position: 'left' | 'left_space' | 'right' | 'right_space';
  decimals: number;
  decimalSeparator: string;
  thousandSeparator: string;
  provider: string;
  valid?: boolean;
  error?: string;
}

export const defaultCurrency: CurrencyContext = {
  baseCode: 'USD',
  code: 'USD',
  symbol: '$',
  position: 'left',
  decimals: 2,
  decimalSeparator: '.',
  thousandSeparator: ',',
  provider: 'legacy',
  valid: true,
};

const currencyEntities: Record<string, string> = {
  amp: '&', apos: "'", cent: '¢', curren: '¤', euro: '€', gt: '>',
  lt: '<', nbsp: '\u00a0', pound: '£', quot: '"', yen: '¥',
};

function decodeCurrencySymbol(symbol: string): string {
  return symbol
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) => String.fromCodePoint(Number.parseInt(code, 16)))
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number.parseInt(code, 10)))
    .replace(/&([a-z]+);/gi, (entity, name: string) => currencyEntities[name.toLowerCase()] ?? entity);
}

export function currencyFromLegacy(
  currency?: Partial<CurrencyContext> | null,
  symbol = '$',
  position = 'left',
): CurrencyContext {
  const normalizedPosition = ['left', 'left_space', 'right', 'right_space'].includes(position)
    ? position as CurrencyContext['position']
    : 'left';
  return {
    ...defaultCurrency,
    symbol,
    position: normalizedPosition,
    ...currency,
  };
}

export function formatMoney(value: number | string, currency?: Partial<CurrencyContext> | null): string {
  const ctx = currencyFromLegacy(currency);
  const symbol = decodeCurrencySymbol(String(ctx.symbol || ''));
  const numeric = Number(value);
  const safe = Number.isFinite(numeric) ? numeric : 0;
  const negative = safe < 0 ? '−' : '';
  const decimals = Math.max(0, Math.min(20, Math.trunc(Number(ctx.decimals) || 0)));
  const [whole, fraction] = Math.abs(safe).toFixed(decimals).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ctx.thousandSeparator);
  const amount = fraction === undefined ? grouped : `${grouped}${ctx.decimalSeparator}${fraction}`;
  const gap = ctx.position.endsWith('_space') ? '\u00a0' : '';
  const formatted = ctx.position.startsWith('right')
    ? `${amount}${gap}${symbol}`
    : `${symbol}${gap}${amount}`;
  return `${negative}${formatted}`;
}
